import React, { useState, useEffect } from "react";
import { Severity } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import {
  SectionCard,
  FormRow,
  PrimaryButton,
  GhostButton,
  ContextualHelpDropdown
} from "@/components/ds";

interface Props {
  teamId: number;
  /** Pre-fill the Account ID field from an existing TBA connection. */
  defaultAccountId?: string;
  /** Called after a config row is created (so the parent can refresh its list). */
  onConnected?: () => void;
}

/**
 * Customer-facing "Connect with OAuth 2.0" card.
 *
 * Mirrors the existing OAuth 1.0a connect pattern (button → redirect to NetSuite →
 * callback page exchanges the code). Backward support for NetSuite's 2027.1 cutoff on
 * CREATING new Token-Based Auth (OAuth 1.0a) integrations — existing OAuth 1.0a
 * connections keep working untouched.
 *
 * Flow:
 *   1. POST NetSuite/Auth/SaveCredentials → creates a connection row holding the
 *      OAuth 2.0 client id/secret (no OAuth1 token), returns its id.
 *   2. GET  NetSuite/OAuth2/AuthUrl/{id}?redirectUri=… → NetSuite consent URL; redirect.
 *   3. NetSuite redirects back to /netsuite/oauth2callback?code&state → that page
 *      POSTs NetSuite/OAuth2/Exchange and the connection flips to AuthType=oauth2.
 */
export default function NetSuiteOAuth2Connect({
  teamId,
  defaultAccountId,
  onConnected
}: Props) {
  const api = useApi();
  const toast = useToastAlert();

  const [displayName, setDisplayName] = useState("");
  const [accountId, setAccountId] = useState(defaultAccountId ?? "");
  const [clientId, setClientId] = useState("");

  // Pre-fill Account ID once the parent resolves existing TBA configs (async load).
  useEffect(() => {
    if (defaultAccountId && !accountId) setAccountId(defaultAccountId);
  }, [defaultAccountId]);
  const [clientSecret, setClientSecret] = useState("");
  const [connecting, setConnecting] = useState(false);

  // NetSuite redirects the browser back to this SPA page. It must EXACTLY match the
  // Redirect URI registered on the NetSuite OAuth 2.0 integration record.
  const redirectUri = `${window.location.origin}/netsuite/oauth2callback`;

  async function connect() {
    if (!accountId.trim() || !clientId.trim() || !clientSecret.trim()) {
      toast.displayToast(
        Severity.Error,
        "Account ID, Client ID and Client Secret are required."
      );
      return;
    }

    setConnecting(true);
    try {
      // 1. Save the connection (client id/secret live in ConsumerKey/ConsumerSecret; no OAuth1 token).
      // Transcript push defaults to off — configure it afterwards in the "Transcript Auto-Push"
      // card under Connected Account below, which is the single source of truth for that setting.
      const saveResp = await api.messaging.post(
        "NetSuite/Auth/SaveCredentials",
        {
          displayName: displayName.trim() || `NetSuite ${accountId.trim()}`,
          company: accountId.trim(),
          consumerKey: clientId.trim(),
          consumerSecret: clientSecret.trim(),
          authType: "oauth2",
          teamId
        }
      );
      if (!saveResp.ok)
        throw new Error(
          `Saving the connection failed (HTTP ${saveResp.status}).`
        );
      const { id } = await saveResp.json();

      onConnected?.();

      // Stash the redirect URI — the callback page only receives code + state from NetSuite.
      sessionStorage.setItem("nsOAuth2RedirectUri", redirectUri);
      // This is a plain Configuration-tab connect, not the netsuitecreate wizard — clear any
      // leftover wizard return-target so the callback never routes this connect back into a
      // stale (possibly abandoned) wizard session.
      sessionStorage.removeItem("nsOAuth2ReturnTo");

      // 2. Fetch the NetSuite consent URL and redirect the browser to it.
      const authResp = await api.messaging.get(
        `NetSuite/OAuth2/AuthUrl/${id}?redirectUri=${encodeURIComponent(redirectUri)}`
      );
      if (!authResp.ok)
        throw new Error(
          `Could not build the authorization URL (HTTP ${authResp.status}).`
        );
      const { url } = await authResp.json();
      window.location.href = url;
    } catch (err) {
      toast.displayToast(
        Severity.Error,
        `OAuth 2.0 connect failed: ${err instanceof Error ? err.message : String(err)}`
      );
      setConnecting(false);
    }
  }

  return (
    <SectionCard
      title="Connect with OAuth 2.0"
      description="Recommended for new connections. NetSuite is retiring the creation of Token-Based Auth (OAuth 1.0a) integrations in 2027.1 — existing connections keep working."
      className="mb-4"
      action={
        <PrimaryButton onClick={connect} disabled={connecting}>
          {connecting ? "Connecting…" : "Connect with OAuth 2.0"}
        </PrimaryButton>
      }
    >
      <div className="space-y-1">
        <FormRow
          label="Connection name"
          hint="A label for this connection (optional)."
        >
          <input
            type="text"
            className="w-full text-sm border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-(--ds-primary)"
            value={displayName}
            placeholder="e.g. Production NetSuite"
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </FormRow>
        <FormRow
          label="Account ID"
          hint="Setup → Company → Company Information → Account ID (e.g. 1234567 or 1234567_SB1)."
        >
          <input
            type="text"
            className="w-full text-sm border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-(--ds-primary)"
            value={accountId}
            placeholder="1234567"
            onChange={(e) => setAccountId(e.target.value)}
          />
        </FormRow>
        <FormRow
          label="Client ID"
          hint="The integration record's Client ID (Consumer Key)."
        >
          <input
            type="text"
            className="w-full text-sm border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-(--ds-primary)"
            value={clientId}
            placeholder="Client ID"
            onChange={(e) => setClientId(e.target.value)}
          />
        </FormRow>
        <FormRow
          label="Client Secret"
          hint="The integration record's Client Secret (Consumer Secret). Shown once by NetSuite."
        >
          <input
            type="password"
            className="w-full text-sm border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-(--ds-primary)"
            value={clientSecret}
            placeholder="Client Secret"
            onChange={(e) => setClientSecret(e.target.value)}
          />
        </FormRow>
      </div>

      <p className="mt-3 text-xs text-zinc-500">
        Transcript auto-push is configured after connecting — see the{" "}
        <strong>Transcript Auto-Push</strong> card under{" "}
        <strong>Connected Account</strong> below.
      </p>

      <div className="mt-3">
        <ContextualHelpDropdown
          title="How do I get these values?"
          panelClassName="!bg-zinc-50 border-transparent! !text-xs !mt-2"
          body={
            <>
              <p className="font-medium text-zinc-800">
                In NetSuite (one-time setup, as an administrator):
              </p>
              <ol className="list-decimal pl-5 space-y-1.5">
                <li>
                  <strong>Enable the features.</strong> Setup → Company → Enable
                  Features → SuiteCloud tab. Check <em>REST Web Services</em>{" "}
                  and <em>OAuth 2.0</em>. Save.
                </li>
                <li>
                  <strong>Create the integration record.</strong> Setup →
                  Integration → Manage Integrations → New. Name it “Velaro”.
                  Under Authentication, check <em>OAuth 2.0</em> and the scope
                  <em> REST Web Services</em>. Uncheck Token-Based
                  Authentication.
                </li>
                <li>
                  <strong>Set the Redirect URI</strong> to exactly:
                  <code className="block mt-1 px-2 py-1 bg-white border border-zinc-200 rounded break-all select-all">
                    {redirectUri}
                  </code>
                </li>
                <li>
                  <strong>Save.</strong> NetSuite shows the <em>Client ID</em>{" "}
                  and <em>Client Secret</em> once — copy both into the fields
                  above. (If you miss them, reset the credentials on the
                  record.)
                </li>
                <li>
                  <strong>Account ID:</strong> Setup → Company → Company
                  Information → Account ID.
                </li>
              </ol>
              <p className="text-zinc-500">
                Click <strong>Connect with OAuth 2.0</strong> — you’ll be sent
                to NetSuite to approve, then returned here. No tokens to copy by
                hand; Velaro stores and refreshes them automatically.
              </p>
            </>
          }
        />
      </div>
    </SectionCard>
  );
}
